import Modal from '@components/common/Modal/Modal';
import Button from '@components/Button/Button';
import Icon from '@components/common/Icon';

export interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
    loading?: boolean;
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'warning' | 'info';
}

const VARIANT_CONFIG = {
    danger: {
        iconName: 'delete' as const,
        iconBg: 'bg-red-50',
        iconColor: '',
        confirmClass: 'bg-red-500 hover:bg-red-600 text-white',
    },
    warning: {
        iconName: 'alert' as const,
        iconBg: 'bg-yellow-50',
        iconColor: '',
        confirmClass: 'bg-yellow-500 hover:bg-yellow-600 text-white',
    },
    info: {
        iconName: 'infoCircle' as const,
        iconBg: 'bg-blue-50',
        iconColor: '',
        confirmClass: 'bg-blue-500 hover:bg-blue-600 text-white',
    },
};

const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    title = 'Are you sure?',
    message = 'This action cannot be undone.',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
}: ConfirmModalProps) => {
    const cfg = VARIANT_CONFIG[variant];

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="max-w-[440px]">
            <div className="flex flex-col items-center gap-4 px-6 py-6 text-center">
                <div className={`flex items-center justify-center w-14 h-14 rounded-full ${cfg.iconBg}`}>
                    <Icon name={cfg.iconName} size={24} alt={variant} className={cfg.iconColor} />
                </div>
                <p className="text-sm text-[var(--secondary-color)] leading-relaxed max-w-[320px]">
                    {message}
                </p>
                <div className="flex gap-3 w-full justify-center mt-2">
                    <Button
                        type="button"
                        variant="dark"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={() => void onConfirm()}
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : confirmLabel}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ConfirmModal;